import { useMemo } from 'react';
import { get, type Optional } from '@direct-frontend/stdlib';
import { type BaseRowData } from '@adv-frontend/data-provider';
import {
    type OpenApiSchemaIntegrationType,
    type OpenApiSchemaOrder,
    type OpenApiSchemaOrderDeliveryInfo,
    type OpenApiSchemaProductVariant,
} from '../../../api/schema';
import { CARD_GRID, ORDER_GRID, PRODUCT_GRID } from '../constants';
import { type GridValidator, type GridValidatorOptions, type GridValidatorType } from './types';

const baseValidator: GridValidator = () => {
    return { statusColor: undefined };
};

const checkCargoboxes = (cargoboxes?: OpenApiSchemaProductVariant['cargoboxes']) =>
    cargoboxes &&
    cargoboxes.length &&
    cargoboxes.every(
        (cargobox) =>
            Boolean(cargobox.height) && Boolean(cargobox.width) && Boolean(cargobox.length) && Boolean(cargobox.weight)
    );

const checkExternalIds = (
    externalIds?: OpenApiSchemaProductVariant['external_ids'],
    requiredSystems?: OpenApiSchemaIntegrationType[],
    status?: OpenApiSchemaProductVariant['status']
): boolean => {
    // Не проверяем внешние ID для архивных товаров
    if (status === 'ARCHIVED') {
        return true;
    }

    if (!requiredSystems || requiredSystems.length === 0) {
        return true;
    }

    if (!externalIds || externalIds.length === 0) {
        return false;
    }

    const existingSystems = new Set<string>(externalIds.map((id) => id.system_type));

    return requiredSystems.every((system) => existingSystems.has(system));
};

const checkHasOrderIntakeError = (deliveryInfo: Optional<OpenApiSchemaOrderDeliveryInfo>) => {
    const interval = deliveryInfo?.shipping_interval;

    return Boolean(!interval?.from && !interval?.to && deliveryInfo?.reschedule_intake_allowed);
};

const createProductGridValidator =
    (options?: GridValidatorOptions): GridValidator =>
    (rowData?: BaseRowData) => {
        const data = get<OpenApiSchemaProductVariant>(rowData, ['attributes']);
        const priceCheck = Boolean(data?.price);
        const cargoboxCheck = checkCargoboxes(data?.cargoboxes);
        const externalIdsCheck = checkExternalIds(data?.external_ids, options?.requiredExternalIdSystems, data?.status);
        const isValidRow = Boolean(priceCheck && cargoboxCheck && externalIdsCheck);

        if (!isValidRow) {
            return { statusColor: 'error' };
        }

        const isMinimalPriceAbovePrice = Number(data?.minimal_price) > Number(data?.price);

        if (isMinimalPriceAbovePrice) {
            return { statusColor: 'warning' };
        }

        return { statusColor: undefined };
    };

const createOrderGridValidator =
    (options?: GridValidatorOptions): GridValidator =>
    (rowData?: BaseRowData) => {
        const data = get<OpenApiSchemaOrder>(rowData, ['attributes']);
        const isSubRow = get<boolean>(rowData, ['isSubRow']);
        const deliveryChunkId = get<Optional<number>>(rowData, ['delivery_chunk_id']);

        let hasIntakeError;

        if (isSubRow) {
            const rowChunk = data?.delivery_chunks.find((chunk) => chunk.id === deliveryChunkId);

            hasIntakeError = checkHasOrderIntakeError(rowChunk?.delivery_info);
        } else {
            hasIntakeError = Boolean(
                data?.delivery_chunks.some((chunk) => checkHasOrderIntakeError(chunk.delivery_info))
            );
        }

        const hasReceiptError = options?.isProcessReceiptsAsyncEnabled && data?.has_receipts_error;
        const isValidRow = !data?.stage_has_error && !hasReceiptError && !hasIntakeError;

        return { statusColor: isValidRow ? undefined : 'error' };
    };

const createCardGridValidator =
    (options?: GridValidatorOptions): GridValidator =>
    (rowData?: BaseRowData) => {
        const data = get(rowData, ['attributes']);
        const variants = get<OpenApiSchemaProductVariant[]>(data, ['variants']);
        const priceCheck = variants?.every((variant) => Boolean(variant.price));
        const cargoboxCheck = variants?.every((variant) => checkCargoboxes(variant.cargoboxes));
        const externalIdsCheck = variants?.every((variant) =>
            checkExternalIds(variant.external_ids, options?.requiredExternalIdSystems, variant.status)
        );
        const isValidRow = Boolean(priceCheck && cargoboxCheck && externalIdsCheck);

        if (!isValidRow) {
            return { statusColor: 'error' };
        }

        const isMinimalPriceAbovePrice = variants?.every(
            ({ minimal_price, price }) => Number(minimal_price) > Number(price)
        );

        if (isMinimalPriceAbovePrice) {
            return { statusColor: 'warning' };
        }

        return { statusColor: undefined };
    };

export const useRowValidator = (grid?: GridValidatorType, options?: GridValidatorOptions): GridValidator =>
    useMemo(() => {
        switch (grid) {
            case PRODUCT_GRID:
                return createProductGridValidator(options);
            case CARD_GRID:
                return createCardGridValidator(options);
            case ORDER_GRID:
                return createOrderGridValidator(options);

            default:
                return baseValidator;
        }
    }, [grid, options]);
